import { useCallback, useMemo } from 'react';
import { ArrayParam } from 'use-query-params';

import { BaseSortState, BaseSortType } from 'noddi-types';
import { type OrderingParam, parseOrderingParams, serializeOrdering, type SortableColumn } from './sortOrdering';

import { useNoddiQueryParam } from '../../../hooks/useNoddiQueryParam';

type SortOrderParams<TColumn extends string> = {
  /** The sorted column, or none -- at most one, in the array shape `useGet` takes. */
  order: BaseSortState<TColumn>[];
  onOrderChange: (column: TColumn, nextOrder: BaseSortType) => void;
};

type Options<TColumn extends string> = {
  /** The table's columns; the URL value is validated against their sort keys. */
  columns?: readonly SortableColumn<TColumn>[];
  /**
   * Used when the URL carries no `ordering`. Authored, so applied as-is --
   * which is why `NoInfer` belongs here, on the only declaration of it: the
   * key is checked against `TColumn` but must never widen it, and a default
   * the columns do not declare would reach the API without validation.
   */
  defaultOrdering?: NoInfer<OrderingParam<TColumn>>;
};

/**
 * Reads and writes the `ordering` query parameter.
 *
 * Pass the table's `columns`: `TColumn` is inferred from their `orderingKey`s
 * (typed off the endpoint's nsdk union via `OrderingKeyOf`), so
 * `onOrderChange` and `defaultOrdering` are checked against it, and the URL
 * value is validated at runtime -- the URL is user-editable, so the type alone
 * cannot vouch for it (see `parseOrderingParams`). The overload without
 * `columns` is the legacy form: untyped and unchecked.
 *
 * One column at a time, both ways: a header choice replaces the current sort,
 * and a multi-key URL is read down to its first usable key.
 */
export function useNoddiSortOrderParams<TColumn extends string>(
  options: Options<TColumn> & { columns: readonly SortableColumn<TColumn>[] }
): SortOrderParams<TColumn>;
export function useNoddiSortOrderParams(options?: { defaultOrdering?: string }): SortOrderParams<string>;
export function useNoddiSortOrderParams<TColumn extends string>({
  columns,
  defaultOrdering
}: Options<TColumn> = {}): SortOrderParams<TColumn> {
  const [orderingParams, setOrderingParams] = useNoddiQueryParam('ordering', ArrayParam);

  const order = useMemo(() => {
    const sort =
      orderingParams == null
        ? parseOrderingParams<TColumn>([defaultOrdering ?? null])
        : parseOrderingParams(orderingParams, columns);
    return sort ? [sort] : [];
  }, [orderingParams, columns, defaultOrdering]);

  const onOrderChange = useCallback(
    (column: TColumn, nextOrder: BaseSortType) => {
      setOrderingParams(serializeOrdering(nextOrder ? { column, order: nextOrder } : null));
    },
    [setOrderingParams]
  );

  return { order, onOrderChange };
}
